/**
 * The chrome's direction (architecture/game-and-show.md U3; plan Track A4, W11).
 *
 * `styles.css` carries one geometry ladder — `--radius-*`, `--blur-*`,
 * `--shadow-*` — and every one of its 313 classes names a rung rather than a
 * number (the lint in `__tests__/chrome-geometry-lint.test.ts` keeps it so).
 * `[data-chrome='surface']` rebinds the whole ladder to the world's own
 * geometry: no corners, no frost, and a shadow thrown by `WALL_SHEAR` the way
 * a building throws one. So the direction is this one attribute.
 *
 * It is deliberately not a preference and not a level: it is a question with
 * two answers, `card` and `surface`, set once at boot so a screenshot can be
 * taken of either. `?chrome=card` keeps the other one reachable, which is how
 * the contact sheet is still made.
 *
 * **W11 is answered: `surface`** (2026-09-27). Three reasons, and the third is
 * the one that settles it:
 *
 *  1. The product is pixel art under an orthographic camera. Nothing in the
 *     world is round, blurred or soft-edged (Q1-Q4, Q9, Q16); a panel that is
 *     round and frosted is the one object in the frame that does not obey the world's geometry.
 *  2. `surface` was already built to the last token — this change is one
 *     constant, which is what the ladder was for.
 *  3. It removes a measured defect rather than an opinion. Q30: two chrome
 *     shots flap at an identical 8x10 box with the same `worldHash`, `card`
 *     and desktop only, and they are the `backdrop-filter` frost. `surface`
 *     has no blur and is stable in every run ever taken of it.
 *
 * Reversible in one word if the owner disagrees: the other direction is still
 * here, still shot by the harness, still proved by its own baseline.
 *
 * Presentation only — nothing here reaches the world or its hash.
 */

export type ChromeDirection = 'card' | 'surface';

/** W11's answer (2026-09-27): a panel is a face in the world, not a card over it. */
export const DEFAULT: ChromeDirection = 'surface';

const DIRECTIONS: readonly ChromeDirection[] = ['card', 'surface'];

/** The direction a query string asks for, or `null` if it does not ask. */
export function requestedDirection(params: URLSearchParams): ChromeDirection | null {
  const asked = params.get('chrome');
  return asked !== null && (DIRECTIONS as readonly string[]).includes(asked)
    ? (asked as ChromeDirection)
    : null;
}

/** Put the direction on the root element, where the ladder is scoped. */
export function applyChromeDirection(
  params: URLSearchParams,
  doc: Document = document,
): ChromeDirection {
  const direction = requestedDirection(params) ?? DEFAULT;
  doc.documentElement.dataset.chrome = direction;
  return direction;
}
